import type React from 'react';
import { useState, useRef, useCallback } from 'react';

type ChildrenValue = {
    ref?: React.MutableRefObject<HTMLDivElement | undefined>;
    isInView: boolean;
    index: number;
};

export const useCarouselChildrenMap = () => {
    // While the map of children can be a ref since we only need it on interaction,
    // determining whether the elements are visible needs to be in
    // state because it controls whether the buttons show or not, and needs to be updated "live"
    const childrenMap = useRef(new Map());
    const [isFirstItemVisible, setIsFirstItemVisible] = useState(true);
    const [isLastItemVisible, setIsLastItemVisible] = useState(false);
    const [lastVisibleItemIndex, setLastVisibleItemIndex] = useState(0);
    const [firstVisibleItemIndex, setFirstVisibleItemIndex] = useState(0);
    const [areAllItemsHidden, setAreAllItemsHidden] = useState(true);
    const [totalCount, setTotalCount] = useState(0);

    const updateChildrenMap = useCallback(
        (index: number, value: ChildrenValue) => {
            childrenMap.current.set(index, value);

            // Filter away items that lack reference.
            // Otherwise we could keep "empty" map entries where there
            // used to be an element reference, but is no longer. This happens
            // for example when switching between Saved Searches on the landing page.
            // One search might contain 48 hits and the next one 10.
            const filteredMap = Array.from(childrenMap.current).filter(
                ([, child]) => {
                    return !!child.ref.current;
                }
            );

            // When switching between different children inside the carousel,
            // like on the landing page with Saved Searches, we are in an intermediate
            // state where all items are hidden. In those cases we cannot rely
            // only on isFirstItemVisible and isLastItemVisible to know whether to render
            // the scroll buttons. If all items are hidden, then we should not show
            // the buttons either. This removes flickering when switching between
            // different children inside the carousel.
            const allHidden = filteredMap.every(([, child]) => !child.isInView);
            setAreAllItemsHidden(allHidden);

            // back to map
            childrenMap.current = new Map(
                filteredMap.map(([key, value]) => [key, value])
            );

            let lastVisibleIndex = -1;
            let firstVisibleIndex = -1;
            // Get and first last visisble item
            for (const key of childrenMap.current.keys()) {
                const currentItem = childrenMap.current.get(key);
                if (
                    currentItem.isInView &&
                    (currentItem.index === childrenMap.current.size - 1 ||
                        !childrenMap.current.get(key + 1)?.isInView)
                ) {
                    lastVisibleIndex = key;
                }
                if (
                    currentItem.isInView &&
                    !childrenMap.current.get(key - 1)?.isInView
                ) {
                    firstVisibleIndex = key;
                }
            }

            setLastVisibleItemIndex(lastVisibleIndex);
            setFirstVisibleItemIndex(firstVisibleIndex);

            setIsFirstItemVisible(firstVisibleIndex === 0);
            setIsLastItemVisible(
                childrenMap?.current.size - 1 === lastVisibleIndex
            );
            setTotalCount(childrenMap.current.size);
        },
        []
    );

    return {
        updateChildrenMap,
        childrenMap: childrenMap.current,
        isFirstItemVisible,
        isLastItemVisible,
        areAllItemsHidden,
        firstVisibleItemIndex,
        lastVisibleItemIndex,
        totalCount
    };
};

export type CarouselChildrenMap = ReturnType<typeof useCarouselChildrenMap>;
